import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type FontSize = 'small' | 'medium' | 'large';
export type AppLanguage = 'en' | 'es' | 'fr' | 'bn';

type TranslationMap = Record<string, string>;
type TranslationOverrides = Partial<Record<AppLanguage, TranslationMap>>;

export const I18N_CONTROL_TERM_KEYS = [
  'dailyStandup',
  'logHours',
  'selectTask',
  'relatedTaskOptional',
  'whatDidYouAccomplishToday',
  'submitStandupNote',
  'hoursWorked',
  'saveChanges',
  'cancel',
  'today',
  'calendar',
  'meetings',
] as const;

export const DEFAULT_CONTROL_TRANSLATIONS: Record<AppLanguage, TranslationMap> = {
  en: {
    dailyStandup: 'Daily Standup',
    logHours: 'Log Hours',
    selectTask: 'Select Task',
    relatedTaskOptional: 'Related Task (Optional)',
    whatDidYouAccomplishToday: 'What Did You Accomplish Today?',
    submitStandupNote: 'Submit Standup Note',
    hoursWorked: 'Hours Worked',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    today: 'Today',
    calendar: 'Calendar',
    meetings: 'Meetings',
  },
  es: {
    dailyStandup: 'Resumen Diario',
    logHours: 'Registrar Horas',
    selectTask: 'Seleccionar Tarea',
    relatedTaskOptional: 'Tarea Relacionada (Opcional)',
    whatDidYouAccomplishToday: 'Que Lograste Hoy?',
    submitStandupNote: 'Enviar Nota Diaria',
    hoursWorked: 'Horas Trabajadas',
    saveChanges: 'Guardar Cambios',
    cancel: 'Cancelar',
    today: 'Hoy',
    calendar: 'Calendario',
    meetings: 'Reuniones',
  },
  fr: {
    dailyStandup: 'Point Quotidien',
    logHours: 'Enregistrer les Heures',
    selectTask: 'Selectionner une Tache',
    relatedTaskOptional: 'Tache Liee (Optionnel)',
    whatDidYouAccomplishToday: 'Quavez Vous Accompli Aujourdhui?',
    submitStandupNote: 'Soumettre la Note',
    hoursWorked: 'Heures Travaillees',
    saveChanges: 'Enregistrer',
    cancel: 'Annuler',
    today: 'Aujourdhui',
    calendar: 'Calendrier',
    meetings: 'Reunions',
  },
  bn: {
    dailyStandup: 'Daily Standup',
    logHours: 'Log Hours',
    selectTask: 'Select Task',
    relatedTaskOptional: 'Related Task (Optional)',
    whatDidYouAccomplishToday: 'What Did You Accomplish Today?',
    submitStandupNote: 'Submit Standup Note',
    hoursWorked: 'Hours Worked',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    today: 'Today',
    calendar: 'Calendar',
    meetings: 'Meetings',
  },
};

interface UserPreferences {
  fontSize: FontSize;
  favoriteProjects: string[];
  language: AppLanguage;
  customTranslations: TranslationOverrides;
}

interface PreferencesContextType {
  preferences: UserPreferences;
  setFontSize: (size: FontSize) => void;
  toggleFavoriteProject: (projectId: string) => void;
  isFavoriteProject: (projectId: string) => boolean;
  setLanguage: (language: AppLanguage) => void;
  getTranslation: (termKey: string, fallback?: string) => string;
  setTranslationTerm: (termKey: string, value: string) => void;
  getLanguageTerms: () => TranslationMap;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

const DEFAULT_PREFERENCES: UserPreferences = {
  fontSize: 'medium',
  favoriteProjects: [],
  language: 'en',
  customTranslations: {},
};

const sanitizePreferences = (raw: unknown): UserPreferences => {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_PREFERENCES;
  }

  const parsed = raw as Partial<UserPreferences>;

  return {
    ...DEFAULT_PREFERENCES,
    ...parsed,
    favoriteProjects: Array.isArray(parsed.favoriteProjects) ? parsed.favoriteProjects : [],
    customTranslations: parsed.customTranslations && typeof parsed.customTranslations === 'object'
      ? parsed.customTranslations
      : {},
    language: parsed.language && DEFAULT_CONTROL_TRANSLATIONS[parsed.language]
      ? parsed.language
      : 'en',
  };
};

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const saved = localStorage.getItem('userPreferences');
    if (!saved) {
      return DEFAULT_PREFERENCES;
    }

    try {
      return sanitizePreferences(JSON.parse(saved));
    } catch {
      return DEFAULT_PREFERENCES;
    }
  });

  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));

    const root = document.documentElement;
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${preferences.fontSize}`);
    root.setAttribute('lang', preferences.language);
  }, [preferences]);

  const mergedLanguageTerms = useMemo(() => {
    const baseTerms = DEFAULT_CONTROL_TRANSLATIONS[preferences.language] || DEFAULT_CONTROL_TRANSLATIONS.en;
    const customTerms = preferences.customTranslations[preferences.language] || {};
    return {
      ...baseTerms,
      ...customTerms,
    };
  }, [preferences.customTranslations, preferences.language]);

  const setFontSize = (size: FontSize) => {
    setPreferences((previous) => ({ ...previous, fontSize: size }));
  };

  const setLanguage = (language: AppLanguage) => {
    setPreferences((previous) => ({ ...previous, language }));
  };

  const setTranslationTerm = (termKey: string, value: string) => {
    setPreferences((previous) => {
      const language = previous.language;
      const languageOverrides = previous.customTranslations[language] || {};
      return {
        ...previous,
        customTranslations: {
          ...previous.customTranslations,
          [language]: {
            ...languageOverrides,
            [termKey]: value,
          },
        },
      };
    });
  };

  const getTranslation = (termKey: string, fallback?: string) => {
    return mergedLanguageTerms[termKey] || fallback || termKey;
  };

  const getLanguageTerms = () => mergedLanguageTerms;

  const toggleFavoriteProject = (projectId: string) => {
    setPreferences((previous) => {
      const favoriteProjects = previous.favoriteProjects.includes(projectId)
        ? previous.favoriteProjects.filter((id) => id !== projectId)
        : [...previous.favoriteProjects, projectId];
      return { ...previous, favoriteProjects };
    });
  };

  const isFavoriteProject = (projectId: string) => {
    return preferences.favoriteProjects.includes(projectId);
  };

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        setFontSize,
        toggleFavoriteProject,
        isFavoriteProject,
        setLanguage,
        getTranslation,
        setTranslationTerm,
        getLanguageTerms,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
